import { useState } from 'react';
import { X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog';

type LegalModalType = 'impressum' | 'datenschutz' | 'agb' | null;

export function Footer() {
  const [openModal, setOpenModal] = useState<LegalModalType>(null);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, type: LegalModalType) => {
    e.preventDefault();
    setOpenModal(type);
  };

  const getModalContent = () => {
    switch (openModal) {
      case 'impressum':
        return {
          title: 'Impressum',
          content: (
            <div className="space-y-4 text-sm text-neutral-700">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Angaben gemäß § 5 TMG</h3>
                <p>gymAhead</p>
                <p className="mt-2">[Ihre Adresse]</p>
                <p>[Ihre PLZ] [Ihr Ort]</p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Kontakt</h3>
                <p>E-Mail: [Ihre E-Mail-Adresse]</p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV</h3>
                <p>[Ihr Name]</p>
                <p>[Ihre Adresse]</p>
                <p>[Ihre PLZ] [Ihr Ort]</p>
              </div>
            </div>
          ),
        };
      case 'datenschutz':
        return {
          title: 'Datenschutzerklärung',
          content: (
            <div className="space-y-4 text-sm text-neutral-700">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">1. Datenschutz auf einen Blick</h3>
                <h4 className="font-medium text-neutral-900 mt-3 mb-2">Allgemeine Hinweise</h4>
                <p>
                  Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, 
                  wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">2. Verantwortliche Stelle</h3>
                <p>Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:</p>
                <p className="mt-2">gymAhead</p>
                <p>[Ihre Adresse]</p>
                <p>[Ihre PLZ] [Ihr Ort]</p>
                <p className="mt-2">E-Mail: [Ihre E-Mail-Adresse]</p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">3. Datenerfassung auf dieser Website</h3>
                <h4 className="font-medium text-neutral-900 mt-3 mb-2">Kontaktformular</h4>
                <p>
                  Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular 
                  inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von 
                  Anschlussfragen bei uns gespeichert.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">4. Ihre Rechte</h3>
                <p>Sie haben jederzeit das Recht:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten</li>
                  <li>Berichtigung unrichtiger Daten zu verlangen</li>
                  <li>Löschung Ihrer bei uns gespeicherten Daten zu verlangen</li>
                  <li>Einschränkung der Datenverarbeitung zu verlangen</li>
                  <li>Widerspruch gegen die Verarbeitung Ihrer Daten einzulegen</li>
                  <li>Datenübertragbarkeit zu verlangen</li>
                </ul>
              </div>
            </div>
          ),
        };
      case 'agb':
        return {
          title: 'Allgemeine Geschäftsbedingungen',
          content: (
            <div className="space-y-4 text-sm text-neutral-700">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">1. Geltungsbereich</h3>
                <p>
                  Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge über die Nutzung der gymAhead-App 
                  und der damit verbundenen Dienstleistungen.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">2. Vertragsgegenstand</h3>
                <p>
                  gymAhead bietet eine mobile Anwendung zur Unterstützung von Trainings- und Fitnessaktivitäten an. 
                  Die App bietet personalisierte Trainingspläne, Fortschrittsverfolgung und weitere fitnessbezogene Funktionen.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">3. Registrierung und Nutzung</h3>
                <p>
                  Zur Nutzung der App ist eine Registrierung erforderlich. Der Nutzer verpflichtet sich, 
                  wahrheitsgemäße und vollständige Angaben zu machen. Jeder Nutzer darf nur ein Konto erstellen.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">4. Preise und Zahlung</h3>
                <p>
                  Die Nutzung der App kann kostenpflichtig sein. Die aktuellen Preise sind in der App angegeben. 
                  Zahlungen erfolgen über die jeweiligen App-Stores (Apple App Store, Google Play Store).
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">5. Kündigung</h3>
                <p>
                  Der Nutzer kann sein Konto jederzeit kündigen. Die Kündigung erfolgt über die Einstellungen in der App 
                  oder per E-Mail an den Support.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-2">6. Haftung</h3>
                <p>
                  gymAhead übernimmt keine Haftung für Schäden, die durch die Nutzung der App entstehen, 
                  soweit diese nicht auf Vorsatz oder grober Fahrlässigkeit beruhen.
                </p>
              </div>
            </div>
          ),
        };
      default:
        return null;
    }
  };

  const modalContent = getModalContent();

  return (
    <>
      <footer className="border-t border-neutral-200 px-6 py-10">
        <div className="mx-auto flex w-full max-w-5xl flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <div className="text-sm text-neutral-500">gymAhead</div>
          <div className="flex flex-wrap gap-4 text-sm text-neutral-500">
            <a
              href="#"
              onClick={(e) => handleLinkClick(e, 'impressum')}
              className="hover:text-neutral-700 transition-colors cursor-pointer"
            >
              Impressum
            </a>
            <a
              href="#"
              onClick={(e) => handleLinkClick(e, 'datenschutz')}
              className="hover:text-neutral-700 transition-colors cursor-pointer"
            >
              Datenschutz
            </a>
            <a
              href="#"
              onClick={(e) => handleLinkClick(e, 'agb')}
              className="hover:text-neutral-700 transition-colors cursor-pointer"
            >
              AGB
            </a>
          </div>
        </div>
      </footer>

      {modalContent && (
        <Dialog open={openModal !== null} onOpenChange={(open) => !open && setOpenModal(null)}>
          <DialogContent 
            className="max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle>{modalContent.title}</DialogTitle>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenModal(null);
                  }}
                  className="p-1 text-neutral-400 hover:text-neutral-600 transition-colors rounded-full hover:bg-neutral-100"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </DialogHeader>
            <div className="mt-4 pr-2">{modalContent.content}</div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
